import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { FaSave, FaTimesCircle, FaPlusCircle, FaFolderOpen, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as FaIcons from 'react-icons/fa'; 

interface Category {
  id: number; 
  name: string;
  icon?: string; 
  parentId?: number | null; 
  subCategories?: Category[]; 
}

const AddCategoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [categoryName, setCategoryName] = useState<string>('');
  const [parentCategory, setParentCategory] = useState<number | null>(null);
  const [existingCategories, setExistingCategories] = useState<Category[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  const fetchExistingCategories = async () => {
    try {
      const response = await fetch('https://ayushvatika.com/api/categories', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data: Category[] = await response.json();
        const categoriesMap = new Map<number, Category>();
        data.forEach(cat => {
          categoriesMap.set(cat.id, { ...cat, subCategories: [] });
        });

        const rootCategories: Category[] = [];
        data.forEach(cat => {
          if (cat.parentId === null || cat.parentId === undefined) {
            rootCategories.push(categoriesMap.get(cat.id)!);
          } else {
            const parent = categoriesMap.get(cat.parentId);
            if (parent) {
              parent.subCategories?.push(categoriesMap.get(cat.id)!);
            } else {
              rootCategories.push(categoriesMap.get(cat.id)!);
            }
          }
        });
        setExistingCategories(rootCategories);
      } else {
        const errorData = await response.json();
        setMessage(`Failed to fetch categories: ${errorData.message}`);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to connect to server.');
      setMessageType('error');
    }
  };

  useEffect(() => {
    fetchExistingCategories();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!categoryName.trim()) {
      setMessage('Category Name cannot be empty.');
      setMessageType('error');
      return;
    }

    try {
      const payload: any = {
        name: categoryName.trim(),
      };
      if (parentCategory !== null) {
        payload.parentId = parentCategory;
      }

      const response = await fetch('https://ayushvatika.com/api/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setMessage('Category added successfully!');
        setMessageType('success');
        setCategoryName('');
        setParentCategory(null);
        fetchExistingCategories();
      } else {
        const errorData = await response.json();
        setMessage(`Failed to add: ${errorData.message}`);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to connect to server.');
      setMessageType('error');
    }
  };

  const handleCancel = () => navigate('/admin/category/list');

  const getFaIcon = (iconName: string, size: number = 16) => {
    const IconComponent = (FaIcons as any)[iconName];
    return IconComponent ? <IconComponent size={size} /> : null;
  };

  const toggleCategoryExpansion = (categoryId: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) newSet.delete(categoryId);
      else newSet.add(categoryId);
      return newSet;
    });
  };

  const renderCategoryTree = (categories: Category[], level: number = 0) => {
    return (
      <ul className={`ml-${level * 4}`}>
        {categories.map(cat => (
          <li key={cat.id} className="mt-2">
            <div className="flex items-center text-gray-700 dark:text-gray-300">
              {cat.subCategories && cat.subCategories.length > 0 && (
                <button
                  type="button"
                  onClick={() => toggleCategoryExpansion(cat.id)}
                  className="mr-2 focus:outline-none hover:text-gray-900 dark:hover:text-white"
                >
                  {expandedCategories.has(cat.id) ? <FaChevronDown size={14} /> : <FaChevronRight size={14} />}
                </button>
              )}
              {(!cat.subCategories || cat.subCategories.length === 0) && (
                <span className="mr-4 w-4"></span>
              )}
              <span className="text-gray-500 dark:text-gray-400 mr-2">{getFaIcon(cat.icon || 'FaFolder', 18)}</span>
              <span className="text-lg font-medium">{cat.name}</span>
            </div>
            {expandedCategories.has(cat.id) && cat.subCategories && cat.subCategories.length > 0 && (
              renderCategoryTree(cat.subCategories, level + 1)
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <AdminLayout>
      <div className="container mx-auto p-6 min-h-screen">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-8 text-center transition-colors">
            Manage Product Categories
        </h1>

        {message && (
          <div className={`p-4 mb-6 rounded-lg text-white text-center font-semibold shadow-md ${messageType === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
            {message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Add New Category Form */}
          <div className="p-8 rounded-2xl shadow-xl transition-colors bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <FaPlusCircle className="mr-3 text-green-500" /> Add New Category
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <label htmlFor="categoryName" className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">
                    Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="categoryName"
                  placeholder="e.g., Organic Vegetables"
                  className="w-full p-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors shadow-sm
                  bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400
                  dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  required
                />
              </div>

              {/* Parent Category Dropdown */}
              <div className="mb-8">
                <label htmlFor="parentCategory" className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">
                    Parent Category (Optional)
                </label>
                <select
                  id="parentCategory"
                  className="w-full p-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors shadow-sm
                  bg-gray-50 border-gray-300 text-gray-900
                  dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  value={parentCategory || ''}
                  onChange={(e) => setParentCategory(e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">No Parent (Top-level Category)</option>
                  {existingCategories.flatMap(cat => [
                    <option key={cat.id} value={cat.id}>{cat.name}</option>,
                    ...(cat.subCategories || []).map(subCat => (
                      <option key={subCat.id} value={subCat.id}>&nbsp;&nbsp;&nbsp;-- {subCat.name}</option>
                    ))
                  ])}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Select an existing category to make this a subcategory.
                </p>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 rounded-lg border transition-colors flex items-center justify-center font-semibold shadow-sm
                  border-gray-300 text-gray-700 hover:bg-gray-100
                  dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <FaTimesCircle className="mr-2" /> Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center justify-center font-semibold shadow-md"
                >
                  <FaSave className="mr-2" /> Add Category
                </button>
              </div>
            </form>
          </div>

          {/* Existing Categories Display */}
          <div className="p-8 rounded-2xl shadow-xl transition-colors bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <FaFolderOpen className="mr-3 text-blue-500" /> Existing Categories
            </h2>
            {existingCategories.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">No categories added yet.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto custom-scrollbar pr-4">
                {renderCategoryTree(existingCategories)}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AddCategoryPage;